<template>
  <form-dialog
    v-model="dialog"
    :fullscreen="isMobileOrIPad"
    :title="$t('Create Expense')"
    persistent
    :disable-submit="uploading || isSaving"
    max-width="560px"
    @close="close"
    @submit="save"
  >
    <template #activator="{ on }">
      <div v-if="!edit">
        <v-btn
          v-if="!quickMode"
          dark
          color="info"
          @click="$emit('open')"
          v-on="on"
          >{{ $t('Create Expense') }}
        </v-btn>
        <v-list-item v-else link v-on="on">
          <div @click="$emit('open')">
            <v-list-item-title class="text-body-2">
              <v-icon class="mr-2">$accounting</v-icon>

              {{ $t('New expense') }}
            </v-list-item-title>
          </div>
        </v-list-item>
      </div>
    </template>
    <!-- Tabs for new expenses -->
    <v-tabs
      v-if="!edit"
      v-model="expenseType"
      color="info"
      slider-color="info"
      class="mb-4"
      fixed-tabs
    >
      <v-tab href="#ClientExpense">{{ $t('Property') }}</v-tab>
      <v-tab v-if="centralBankView || quickMode" href="#BusinessExpense">
        {{ $t('Business') }}
      </v-tab>
      <v-tab
        v-if="isAdmin && managementPage && !quickMode"
        href="#MonthlyExpense"
      >
        {{ $t('Monthly') }}
      </v-tab>
    </v-tabs>

    <!-- Expense type selector for editing -->
    <v-select
      v-if="edit"
      v-model="expenseType"
      :items="expenseTypeOptions"
      :label="$t('Expense Type')"
      :disabled="!canChangeExpenseType"
      :hint="
        canChangeExpenseType
          ? ''
          : $t('Cannot change type for reconciled expenses')
      "
      persistent-hint
      outlined
      dense
      class="mb-4"
    />
    <multi-property-picker
      v-if="
        isMonthlyExpense ||
        isClientExpense ||
        (expenseType === 'ClientExpense' && !editExpense.listing_id)
      "
      v-model="listingId"
      :value="fixedListingId"
      :custom-listings="listingsActiveAndDups"
      :close-on-content-click="true"
      :rules="[required]"
      :disabled="Boolean(fixedListingId || (edit && originalListingId))"
      :readonly="Boolean(fixedListingId || (edit && originalListingId))"
    />
    <listing-select
      v-if="listingId && selectedHotel"
      :label="$t('Choose Unit')"
      item-text="nickname"
      close-on-select
      :hide-title="true"
      :hide-details="false"
      :hotel-id="listingId"
      :pre-selected.sync="unitID"
    />
    <expenses-types-autocomplete
      v-model="expense.shortDescription"
      :is-business-expense="isBusinessExpense"
      :outlined="true"
      :rules="[required]"
      :label="$t('Expense Type')"
    />
    <v-text-field
      v-model="expense.amount"
      :rules="[required]"
      :label="$t('Amount')"
      type="number"
      required
      outlined
      dense
    />
    <div
      v-if="
        listingId &&
        Array.isArray(listingId) &&
        listingId.length > 1 &&
        expense.amount
      "
      class="mt--1 mb-3 blue--text"
    >
      <v-icon class="mr-2">info</v-icon>
      {{ toMoney(expense.amount / listingId.length) }} {{ $t('per listing') }}
    </div>

    <!-- Owner Split Percentage Slider -->
    <v-card v-if="showOwnerSplitSlider" class="mb-4 pa-3" outlined>
      <div class="text-subtitle-2 mb-2">
        {{ $t('Owner/Company Cost Split') }}
      </div>
      <v-slider
        v-model="expense.owner_split_override"
        :min="0"
        :max="100"
        :step="1"
        thumb-label="always"
        color="primary"
        track-color="grey"
        class="mt-4"
      >
        <template #prepend>
          <span class="text-caption">0%</span>
        </template>
        <template #append>
          <span class="text-caption">100%</span>
        </template>
        <template #thumb-label="{ value }"> {{ value }}% </template>
      </v-slider>
      <div class="d-flex justify-space-between text-caption">
        <div>
          <strong>{{ $t('Owner pays:') }}</strong>
          <span class="primary--text">
            {{ expense.owner_split_override }}% ({{
              toMoney(
                (expense.amount * expense.owner_split_override) / 100 || 0
              )
            }})
          </span>
        </div>
        <div>
          <strong>{{ $t('Company pays:') }}</strong>
          <span class="secondary--text">
            {{ 100 - expense.owner_split_override }}% ({{
              toMoney(
                (expense.amount * (100 - expense.owner_split_override)) / 100 ||
                  0
              )
            }})
          </span>
        </div>
      </div>
    </v-card>

    <v-combobox
      v-model="expense.source"
      :items="sources"
      :rules="[required]"
      :label="$t('Store/Source')"
      outlined
      dense
      :placeholder="$t('Amazon/Home depot etc..')"
      required
      clearable
      small-chips
      :allow-overflow="false"
      :hide-selected="false"
      autocomplete="off"
      :search-input.sync="sourceSearch"
      :loading="isLoadingSources"
      :filter="() => true"
      @click:clear="clearSource"
    />
    <date-picker
      v-model="expense.date"
      :hide-details="false"
      :rules="[required]"
      :label="isMonthlyExpense ? $t('Starting At') : $t('Paid At')"
      :max="isAdmin ? null : $moment().endOf('month').toISOString()"
    />
    <date-picker
      v-if="isMonthlyExpense"
      v-model="expense.deactivated_at"
      :hide-details="false"
      :label="$t('Until (leave empty for endless)')"
      :max="$moment().add(2, 'years').toISOString()"
      min="2017-01-01"
    />
    <v-text-field
      v-model="expense.description"
      outlined
      dense
      color="deep-purple"
      :label="$t('Description')"
      rows="2"
    />
    <contractor-select
      v-model="expense.contractor_id"
      :label="$t('Payable contractor')"
      hide-details
      :hint="
        $t(
          'By choosing contractor it will be payable to the contractor via the contractor payments'
        )
      "
    />
    <v-row v-if="config.has_xero">
      <v-col cols="12">
        <v-autocomplete
          v-model="defaultExpenseAccount"
          item-text="account_name"
          item-value="id"
          :label="$t('Xero Account')"
          dense
          outlined
          hide-details
          clearable
          :allow-overflow="false"
          :disabled="edit"
          :items="xeroAccounts"
        />
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="invoice.invoice_number"
          outlined
          dense
          hide-details
          color="deep-purple"
          :label="$t('Invoice Number')"
          rows="2"
        />
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="invoice.reference"
          outlined
          hide-details
          dense
          color="deep-purple"
          :label="$t('Reference')"
          rows="2"
        />
      </v-col>
      <v-col cols="12">
        <date-picker
          v-model="invoice.due_date"
          :value="parseDate(invoice.due_date)"
          details
          :hide-details="false"
          :label="$t('Due Date')"
          :max="$moment().add(2, 'years').toISOString()"
          min="2017-01-01"
        />
      </v-col>
    </v-row>
    <media-upload
      :key="fileKey"
      class="d-flex flex-column"
      folder="expenses"
      :preview="true"
      :multiple="true"
      :thumbnail="true"
      :compress="false"
      :loader="true"
      :preview-cols="6"
      :uploading.sync="uploading"
      :removeable-image="true"
      :value.sync="files"
    />
  </form-dialog>
</template>

<script>
import axios from 'axios'
import CommonFunctions from 'components/mixins/common_functions'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import Toaster from '@/utils/toaster'
import DatePicker from 'components/form-fields/date-picker'
import MediaUpload from 'components/common/media-upload'
import ListingSelect from 'components/listing-select'
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import FormDialog from 'components/common/form-dialog'
import { mapGetters, mapState, mapActions } from 'vuex'
import { get } from 'lodash'
import DeviceMixin from 'components/mixins/device-mixin'
import MultiPropertyPicker from 'components/multi-property-picker.vue'
import FormattersMixin from 'components/mixins/formatters-mixin'
import ExpensesTypesAutocomplete from 'components/expenses-types-autocomplete.vue'
import ContractorSelect from 'components/contractor-select'
import debounce from 'lodash/debounce'

export default {
  components: {
    MultiPropertyPicker,
    FormDialog,
    ListingSelect,
    MediaUpload,
    DatePicker,
    ExpensesTypesAutocomplete,
    ContractorSelect,
  },
  mixins: [
    CommonFunctions,
    PermissionsMixin,
    FormRulesMixin,
    DeviceMixin,
    FormattersMixin,
  ],
  props: [
    'type',
    'title',
    'allowToggle',
    'quickMode',
    'fixedListingId',
    'centralBankView',
    'preFilledAmount',
    'preFilledDate',
    'preFilledDesc',
    'edit',
    'connectedTransactionId',
    'relatedToSetup',
    'editExpense',
  ],
  data() {
    let setDate = this.preFilledDate
      ? new Date(this.preFilledDate).toISOString().substr(0, 10)
      : new Date().toISOString().substr(0, 10)
    return {
      myId: Math.floor(Math.random() * 100000) + 1,
      search: null,
      date: setDate,
      files: [],
      fileKey: Math.random(),
      bankAccountId: null,
      xeroAccountId: null,
      uploading: false,
      listingId: this.fixedListingId,
      originalListingId: null,
      dialog: false,
      unitID: null,
      expenseType: 'ClientExpense',
      defaultExpenseAccount: this.$store.state.app.xeroDefaultExpenseId,
      expense: {
        amount: this.preFilledAmount,
        shortDescription: '',
        date: setDate,
        deactivated_at: '',
        description: this.preFilledDesc,
        paid_by_us: true,
        related_to_setup: this.relatedToSetup ? true : false,
        source: '',
        contractor_id: null,
        owner_split_override: null,
      },
      selectedExpenseType: null,
      invoice: {
        xero_contact_id: null,
        invoice_number: '',
        reference: '',
        due_date: setDate,
        bank_account_id: null,
        amount: null,
      },
      sourceSearch: null,
      isLoadingSources: false,
      isSaving: false,
    }
  },
  computed: {
    ...mapState('expenseTypes', ['expensesTypes']),
    ...mapGetters(['listingsActiveAndDups']),
    ...mapGetters('users', ['usersList', 'usersMap']),
    config() {
      return this.$store.state.app.configuration
    },
    source() {
      return this.expense.source
    },
    selectedHotel() {
      if (this.listingId) {
        return this.getListingById(this.listingId).listing_type === 'hotel'
      }
      return false
    },
    accountNumbers() {
      return this.$store.state.app.bankAccounts.map(ba => ({
        value: ba.id,
        text: `${
          ba.first_name || ba.last_name
            ? `${ba.first_name} ${ba.last_name}`
            : 'Company'
        } (${ba.account_number})`,
      }))
    },
    xeroAccounts() {
      return this.$store.state.app.xeroAccounts.filter(
        c => c.department === 'expense'
      )
    },
    amount() {
      return this.expense.amount
    },
    managementPage() {
      return this.$route.name === 'manage-property'
    },
    isClientExpense() {
      return this.expenseType === 'ClientExpense'
    },
    isMonthlyExpense() {
      return this.expenseType === 'MonthlyExpense'
    },
    isBusinessExpense() {
      return this.expenseType === 'BusinessExpense'
    },
    showOwnerSplitSlider() {
      // Show slider only for ClientExpense when a listing is selected
      // and that listing has a split percentage configured (< 100%)
      if (
        this.expenseType !== 'ClientExpense' ||
        !this.listingId ||
        (Array.isArray(this.listingId) && this.listingId.length > 1)
      ) {
        return false
      }
      const listingId = Array.isArray(this.listingId)
        ? this.listingId[0]
        : this.listingId
      const listing = this.getListingById(listingId)
      return (
        listing &&
        listing.owner_split_percentage !== undefined &&
        listing.owner_split_percentage !== null &&
        listing.owner_split_percentage < 100
      )
    },
    canChangeExpenseType() {
      // Allow type changes only for non-reconciled expenses during edit
      if (!this.edit || !this.editExpense) {
        return true
      }
      return (
        !this.editExpense.is_fully_reconciled &&
        !this.editExpense.is_partially_reconciled
      )
    },
    expenseTypeOptions() {
      // Options for the expense type selector during edit
      const options = [
        { text: this.$t('Property'), value: 'ClientExpense' },
        { text: this.$t('Business'), value: 'BusinessExpense' },
      ]
      return options
    },
    ...mapState('expenses', ['sources']),
  },
  mounted() {
    this.$store.dispatch('getXeroAccounts')
    window.$store.state.expenseModal = this
    this.defaultExpenseAccount = this.$store.state.app.xeroDefaultExpenseId
    if (this.editExpense) {
      this.setExpense(this.editExpense)
    }
    // Set initial owner_split_override if listing is already selected
    if (this.listingId && this.isClientExpense) {
      const listing = this.getListingById(this.listingId)
      if (
        listing &&
        listing.owner_split_percentage !== undefined &&
        listing.owner_split_percentage !== null &&
        listing.owner_split_percentage < 100
      ) {
        this.expense.owner_split_override = listing.owner_split_percentage
      }
    }
  },
  created() {
    if (!this.expensesTypes.length) {
      this.fetchAllExpenseTypes()
    }
    this.fetchSources()
    this.debouncedSearch = debounce(async val => {
      this.isLoadingSources = true
      try {
        await this.fetchSources(val)
      } finally {
        this.isLoadingSources = false
      }
    }, 300)
  },
  beforeDestroy() {
    this.debouncedSearch.cancel()
  },
  watch: {
    editExpense(newVal) {
      if (newVal) {
        this.setExpense(newVal)
      }
    },
    expenseType(newVal) {
      if (newVal === 'BusinessExpense') {
        this.listingId = null
      } else if (
        newVal === 'ClientExpense' &&
        this.edit &&
        this.originalListingId
      ) {
        this.listingId = this.originalListingId
      }
    },
    defaultExpenseAccountId(newVal) {
      if (newVal) {
        this.invoice.bank_account_id = newVal
      }
    },
    shortDescription(newVal) {
      this.selectedExpenseType = this.expensesTypes.find(a => a.name === newVal)
    },
    sourceSearch(val) {
      this.debouncedSearch(val || '')
    },
    dialog(newVal) {
      // When dialog opens, set initial owner_split_override if listing is selected
      if (newVal && this.listingId && this.isClientExpense) {
        const listing = this.getListingById(this.listingId)
        if (
          listing &&
          listing.owner_split_percentage !== undefined &&
          listing.owner_split_percentage !== null &&
          listing.owner_split_percentage < 100
        ) {
          this.expense.owner_split_override = listing.owner_split_percentage
        }
      }
    },
    listingId(newVal) {
      // When listing changes, set the default owner_split_override from the listing's configuration
      if (newVal && this.isClientExpense) {
        const listingId =
          Array.isArray(this.listingId) && this.listingId.length === 1
            ? this.listingId[0]
            : this.listingId
        const listing = this.getListingById(listingId)
        if (
          listing &&
          listing.owner_split_percentage !== undefined &&
          listing.owner_split_percentage !== null &&
          listing.owner_split_percentage < 100
        ) {
          // Default to the listing's configured split percentage
          this.expense.owner_split_override = listing.owner_split_percentage
        } else {
          this.expense.owner_split_override = null
        }
      }
    },
  },
  methods: {
    ...mapActions('expenseTypes', ['fetchAllExpenseTypes']),
    ...mapActions('expenses', ['fetchSources']),
    setExpense(expense) {
      if (expense) {
        this.dialog = true
        this.expenseType = expense.type
        let deactivatedAt = null
        if (expense.deactivated_at) {
          deactivatedAt = new Date(expense.deactivated_at)
            .toISOString()
            .substr(0, 10)
        }
        this.listingId = expense.listing_id
        this.originalListingId = expense.listing_id
        this.unitID = expense.unit_id
        this.bankAccountId = expense.bank_account_id
        this.files = (expense.images || []).map(img => {
          if (
            typeof img === 'string' &&
            img.startsWith('{') &&
            img.includes('"url"=>"')
          ) {
            const match = img.match(/"url"=>"([^"]+)"/)
            return match ? match[1] : img
          }
          return img
        })

        this.expense = {
          shortDescription: expense.short_reason,
          description: expense.description,
          bank_account_id: expense.bank_account_id,
          xero_contact_id: expense.xero_contact_id,
          listing_id: expense.listing_id,
          unit_id: expense.unit_id,
          type: expense.type,
          // Use UTC mode to extract date without timezone conversion
          // This ensures the date displayed matches the date stored (UTC noon)
          date: this.$moment.utc(expense.paid_at).format('YYYY-MM-DD'),
          deactivated_at: deactivatedAt,
          amount: expense.amount,
          paid_by_us: expense.paid_by_us,
          source: expense.source,
          id: expense.id,
          related_to_setup: expense.related_to_setup,
          contractor_id: expense.contractor_id,
          owner_split_override: expense.owner_split_override,
        }

        this.invoice = {
          xero_contact_id: expense.contractor_id,
          invoice_number: expense.xero_invoice?.invoice_number || '',
          bank_account_id: this.defaultExpenseAccount,
          amount: expense.amount,
          reference: expense.xero_invoice?.reference || '',
          due_date: expense.xero_invoice?.due_date
            ? new Date(expense.xero_invoice.due_date)
                .toISOString()
                .substr(0, 10)
            : new Date().toISOString().substr(0, 10),
        }
      }
    },
    close() {
      const setDate = this.preFilledDate
        ? new Date(this.preFilledDate).toISOString().substr(0, 10)
        : new Date().toISOString().substr(0, 10)

      this.expense = {
        amount: this.preFilledAmount,
        shortDescription: '',
        date: setDate,
        deactivated_at: '',
        description: this.preFilledDesc,
        paid_by_us: true,
        related_to_setup: this.relatedToSetup ? true : false,
        source: '',
        contractor_id: null,
        owner_split_override: null,
      }
      this.files = []
      this.fileKey = Math.random()
      this.listingId = this.fixedListingId
      this.originalListingId = null
      this.bankAccountId = null
      this.xeroAccountId = null
      this.expenseType = 'ClientExpense'
      this.isSaving = false
      this.$store.commit('updateEditExpense', null)
    },
    save() {
      if (this.isSaving) return
      this.isSaving = true

      if (this.edit) {
        this.expense.short_reason = this.expense.shortDescription
        this.expense.paid_at = this.expense.date
        this.expense.unit_id = this.unitID
        this.expense.images = this.files
        this.invoice.amount = this.expense.amount

        const originalType = this.editExpense?.type
        const typeChanged = originalType !== this.expenseType

        if (this.canChangeExpenseType && typeChanged) {
          this.expense.type = this.expenseType
          if (this.expenseType === 'BusinessExpense') {
            this.expense.listing_id = null
          } else if (this.expenseType === 'ClientExpense') {
            this.expense.listing_id = this.listingId
            if (!this.listingId) {
              Toaster.show([
                {
                  type: 'error',
                  text: this.$t(
                    'Please select a property for property expenses'
                  ),
                },
              ])
              return
            }
          }
        } else if (!typeChanged && this.expenseType === 'ClientExpense') {
          this.expense.listing_id = this.listingId
        }

        axios
          .post('/api/expenses/' + this.expense.id, this.expense)
          .then(() => {
            this.$store.dispatch(
              'getExpensesByFilter',
              this.$store.state.lastExpensesFilter
            )
            Toaster.show([
              {
                type: 'success',
                text: this.$t('Expense updated successfully'),
              },
            ])
          })
          .catch(error => {
            this.$store.commit('updateLoading', false)
            if (
              error.response &&
              error.response.data &&
              error.response.data.type
            ) {
              Toaster.show([
                { type: 'error', text: error.response.data.type[0] },
              ])
            } else {
              Toaster.show([
                { type: 'error', text: this.$t('Failed to update expense') },
              ])
            }
          })
          .finally(() => {
            this.isSaving = false
            this.close()
          })
      } else {
        let data = {}
        data.amount = this.expense.amount
        data.short_reason = this.expense.shortDescription
        data.description = this.expense.description
        data.unit_id = this.unitID
        data.source = this.expense.source
        data.bank_account_id = this.bankAccountId
        data.xero_contact_id = this.expense.contractor_id
        data.contractor_id = this.expense.contractor_id
        this.invoice.amount = this.expense.amount
        data.xero_invoice = this.invoice
        data.xero_invoice.bank_account_id = this.defaultExpenseAccount
        data.xero_invoice.xero_contact_id = this.expense.contractor_id
        if (this.expense.related_to_setup) {
          data.related_to_setup = this.expense.related_to_setup
        }
        data.type = this.expenseType
        data.paid_by_us = this.expense.paid_by_us
        if (this.isClientExpense || this.isMonthlyExpense) {
          data.listing_id = this.listingId
        }
        if (
          this.expense.owner_split_override !== null &&
          this.expense.owner_split_override !== undefined
        ) {
          data.owner_split_override = this.expense.owner_split_override
        }
        if (this.connectedTransactionId) {
          data.connected_transaction_id = this.connectedTransactionId
        }
        data.paid_at = this.expense.date
        data.deactivated_at = this.expense.deactivated_at

        if (this.files.length) {
          data.images = this.files
        }
        this.$store.commit('updateListingLoading', true)
        axios
          .post('/api/expenses/', data)
          .then(res => {
            const expId = get(res, 'data.payload.data.id')
            const link = expId ? `/dashboard/expenses/${expId}` : undefined
            Toaster.show([
              {
                type: 'success',
                text: this.$t('Expense Created successfully'),
                link,
              },
            ])
            this.$store.dispatch(
              'getExpensesByFilter',
              this.$store.state.lastExpensesFilter
            )
            this.$store.commit('updateListingLoading', false)
            if (this.connectedTransactionId) {
              this.$store.dispatch('refreshBankTransaction')
            }
          })
          .catch(() => {
            Toaster.show([
              { type: 'error', text: this.$t('Failed to create expense') },
            ])
            this.$store.commit('updateLoading', false)
          })
          .finally(() => {
            this.isSaving = false
            this.close()
          })
      }

      this.$emit('on-submit')
    },
    async clearSource() {
      this.expense.source = null
      this.sourceSearch = null
      this.isLoadingSources = true
      try {
        await this.fetchSources('')
      } finally {
        this.isLoadingSources = false
      }
    },
  },
}
</script>
